// hooks/useRoomWs.js: M3-13b real WS hook.
//
// Wraps createRoomWsService (M3-13a) with a useReducer that maps server
// events to a room-shaped view: {status, roomCode, occupants, isHost,
// settings, phase, error, removed}. Surface is identical to the M3-10
// stub so RoomLobby/RoomSettings/TeamColumns etc. didn't change.
//
// Connection status comes from the WS service via onStatusChange. Room
// state derives entirely from server events, the hook does not
// optimistically mutate room state; the server holds the real state.

import { useReducer, useCallback, useMemo, useRef, useEffect } from 'preact/hooks';
import { getOrCreateIdentity } from '../identity/identity.js';
import { createRoomWsService } from '../ws/room-ws.js';
import { parseRoomCodeFromUrl, shouldAutoJoin } from '../nav/room-url.js';
import { seatsAtGameStart } from '../game/occupant-seats.js';
import { reportServiceError } from '../ws/service-error-notice.js';
import { clearLastRoom, lastRoomEffect } from '../nav/last-room.js';

const DEFAULT_SETTINGS = Object.freeze({
  target_score:       10,
  hand_timer_seconds: 20,
  stick_the_dealer:   true,
  bot_fill:           true,
});

/**
 * @typedef {{
 *   occupant_id: string,
 *   display_name: string,
 *   avatar_color: string,
 *   is_host: boolean,
 *   is_you: boolean,
 *   connection_status: 'connected'|'lagging'|'disconnected',
 *   ready: boolean,
 *   team: null|'A'|'B',
 * }} RoomViewOccupant
 */

export const INITIAL = {
  /** @type {'idle'|'connecting'|'creating'|'joining'|'in_room'|'error'} */
  status: 'idle',
  /** @type {string|null} */
  roomCode: null,
  /** @type {string|null} */
  selfOccupantId: null,
  /** @type {RoomViewOccupant[]} */
  occupants: [],
  isHost: false,
  /** @type {{target_score:7|10, hand_timer_seconds:20|30|60|null, stick_the_dealer:boolean, bot_fill:boolean}} */
  settings: { ...DEFAULT_SETTINGS },
  /** @type {'waiting'|'starting'|'in_game'} */
  phase: 'waiting',
  /** @type {{reason:'kicked'|'room_closed'}|null} */
  removed: null,
  /** @type {{code:string, message:string}|null} */
  error: null,
  lastGame: null,
  viewerSeatIndex: null,
  /** The code of the join in flight (or the last failed one), so a
   * ROOM_NOT_FOUND can be matched to the remembered room. */
  /** @type {string|null} */
  joiningCode: null,
};

/**
 * Plain words for the server's room error codes (B2-4). Codes not
 * listed here keep the server message; ALREADY_CONNECTED keeps the
 * tab-specific copy set below.
 *
 * @param {string} code
 * @param {string|null} commandType
 * @returns {string|null} the friendly message, or null to keep the server text
 */
export function friendlyRoomError(code, commandType) {
  switch (code) {
    case 'ROOM_NOT_FOUND':
      return 'No room with that code.';
    case 'ROOM_FULL':
      return 'That room is full.';
    case 'KICKED':
      return 'The host removed you from this room.';
    case 'GAME_ALREADY_STARTED':
      // B4a-F2: a player who arrives mid-game (rejoin after Leave, or a
      // late friend) cannot rejoin — the seat is bot-held to the end of
      // the game. Say when they can join, on the home screen that keeps
      // the Create/Join buttons (the way back home).
      return commandType === 'JOIN_ROOM'
        ? 'This game is in progress. You can join when it ends.'
        : null;
    default:
      return null;
  }
}

/** Map server's public occupant (snake_case) → view occupant (with is_you). */
function toViewOccupant(serverOcc, selfOccupantId) {
  return {
    occupant_id:       serverOcc.occupant_id,
    display_name:      serverOcc.display_name,
    avatar_color:      serverOcc.avatar_color,
    is_host:           !!serverOcc.is_host,
    is_you:            serverOcc.occupant_id === selfOccupantId,
    connection_status: serverOcc.connection_status || 'connected',
    ready:             !!serverOcc.is_ready,
    seat_index:        serverOcc.seat_index ?? null, // M4-11
    team:              serverOcc.team ?? null,       // #8b: null|'A'|'B'
    join_order:        serverOcc.join_order ?? null, // item 2: seating order
  };
}

/**
 * fix9 J1: does this room state hold a room the server has evicted us
 * from, so the hook must forget it? True for a kick/removal (`removed`,
 * the W5 path) and for a NOT_IN_ROOM exit (the B2-13 error shape: the
 * reducer leaves the room view with roomCode null but `removed` stays
 * null, so the effect below would never fire without this second arm).
 * Either way the hook calls the service's forgetRoom(), and the next
 * reconnect replays no JOIN_ROOM for the dead room.
 *
 * @param {{ removed: unknown, status: string, error: { code?: string }|null }} state
 */
export function needsRoomForget(state) {
  if (state.removed) return true;
  return state.status === 'error' && state.error?.code === 'NOT_IN_ROOM';
}

/**
 * fix10 K2: the forgetRoom effect's decision as a pure, exported
 * function so a unit test can pin it. Calls svc.forgetRoom() exactly
 * when needsRoomForget(state) holds (kick/removal or a NOT_IN_ROOM
 * exit). The hook effect below calls this; testing through this
 * function fails if its check is reverted to `state.removed` because
 * a NOT_IN_ROOM exit has `removed` null and would never forget.
 *
 * @param {{ removed: unknown, status: string, error: { code?: string }|null }} state
 * @param {{ forgetRoom?: () => void }|null|undefined} svc
 */
export function roomForgetEffect(state, svc) {
  if (!needsRoomForget(state)) return;
  if (!svc || typeof svc.forgetRoom !== 'function') return;
  try { svc.forgetRoom(); } catch (err) { console.warn('[useRoomWs] forgetRoom after removal failed:', err instanceof Error ? err.message : err); }
}

export function forgetRoomEffectDeps(state) {
  return [state.removed, state.status, state.error];
}

export function lastGameText(lastGame) {
  if (!lastGame?.scores || !lastGame?.winnerTeam || !lastGame?.viewerTeam) return '';
  const won = lastGame.winnerTeam === lastGame.viewerTeam;
  const other = lastGame.winnerTeam === 'team_a' ? 'team_b' : 'team_a';
  return `Last game: ${won ? 'you won' : 'they won'} ${lastGame.scores[lastGame.winnerTeam]}–${lastGame.scores[other]}`;
}

/**
 * The last-game banner data, or null when the result is not known (no
 * winner, as after an engine failure, or no scores): never an empty banner.
 * @param {any} scores
 * @param {any} winnerTeam
 * @param {number|null|undefined} viewerSeatIndex
 */
function lastGameFrom(scores, winnerTeam, viewerSeatIndex) {
  if (winnerTeam !== 'team_a' && winnerTeam !== 'team_b') return null;
  if (typeof scores?.team_a !== 'number' || typeof scores?.team_b !== 'number') return null;
  if (viewerSeatIndex == null) return null;
  return {
    scores: { team_a: scores.team_a, team_b: scores.team_b },
    winnerTeam,
    viewerTeam: viewerSeatIndex % 2 === 0 ? 'team_a' : 'team_b',
  };
}

/**
 * Item 4 (round 4): the service's onError. Every error is warned with its
 * code; the ones the player can act on go to the room state (shown on
 * the home screen).
 * @param {(action: any) => void} dispatch
 */
export function serviceErrorHandler(dispatch) {
  return (/** @type {{ code: string, message?: string }} */ err) =>
    reportServiceError(err, (notice) => dispatch({ type: 'SERVICE_ERROR', error: notice }));
}

export function scheduleJoinTimeout(onTimeout, scheduler = setTimeout, canceller = clearTimeout) {
  const timer = scheduler(onTimeout, 10_000);
  return () => canceller(timer);
}

export function reducer(state, action) {
  switch (action.type) {
    case 'CONNECT_PENDING':
      return { ...state, status: 'connecting' };
    case 'CREATE_PENDING':
      return { ...state, status: 'creating', error: null };
    case 'JOIN_PENDING':
      return { ...state, status: 'joining', error: null, joiningCode: action.code ?? null };
    case 'JOIN_TIMEOUT':
      return state.status === 'joining'
        ? { ...state, status: 'error', error: { code: 'JOIN_TIMEOUT', message: 'Could not reach the room. Try again.' } }
        : state;
    case 'COMMAND_REJECTED': {
      // PING rejections must never knock the room out, heartbeats are
      // unrelated to user-visible state. For other commands: only flip
      // status to 'error' when we're outside a room (lobby create/join
      // failed); inside a room we surface the error transiently instead
      // of dropping the user back to the lobby.
      if (action.commandType === 'PING') return state;
      // B2-13: NOT_IN_ROOM means the server no longer counts this
      // socket as an occupant (leave, kick, or a stale socket). In a
      // room the client leaves the room view with a plain message and
      // shows no toast; outside a room the stale frame is ignored.
      if (action.error?.code === 'NOT_IN_ROOM') {
        if (state.status === 'joining' && action.commandType === 'JOIN_ROOM') {
          return { ...state, status: 'error', error: { code: 'NOT_IN_ROOM', message: 'No room with that code.' } };
        }
        if (state.status !== 'in_room') return state;
        return {
          ...INITIAL,
          status: 'error',
          error: { code: 'NOT_IN_ROOM', message: 'You are no longer in this room.' },
        };
      }
      // #qa-D2: ALREADY_CONNECTED almost always means a same-identity second
      // tab opened the room URL while the first tab still holds the seat. Swap
      // the raw server wording for friendly, tab-specific copy. Crucially this
      // makes a rejection-while-'joining' RECOVERABLE, flipping to 'error'
      // (via the path below) so the second tab can never wedge on "Joining…".
      const friendly = friendlyRoomError(action.error?.code, action.commandType);
      const error = action.error?.code === 'ALREADY_CONNECTED'
        ? { ...action.error, message: 'This room is already open in another tab.' }
        : friendly != null
          ? { ...action.error, message: friendly }
          : action.error;
      if (state.status === 'in_room') {
        return { ...state, error };
      }
      // W5: outside a room, a rejection answers the user's own join or
      // create only while that request is in flight. A stale frame (a
      // reconnect replay racing a leave, a late duplicate) arriving while
      // idle — or while an old error is already showing — is ignored so
      // the home screen never shows a room error the user didn't cause.
      // fix10 K1: while joining only a JOIN_ROOM rejection answers the
      // in-flight request; while creating only CREATE_ROOM does. Any
      // other command (e.g. a late BID/NOT_YOUR_TURN racing the join)
      // is ignored so it can never replace the real join/create answer.
      if (state.status === 'joining' && action.commandType !== 'JOIN_ROOM') return state;
      if (state.status === 'creating' && action.commandType !== 'CREATE_ROOM') return state;
      if (state.status !== 'creating' && state.status !== 'joining') return state;
      return { ...state, status: 'error', error };
    }
    case 'SERVICE_ERROR':
      // In a room the room stays; the game view's banner covers the socket.
      return state.status === 'in_room'
        ? { ...state, error: action.error }
        : { ...state, status: 'error', error: action.error };
    case 'CLEAR_ERROR':
      return { ...state, error: null, status: state.roomCode ? 'in_room' : 'idle' };
    case 'ROOM_CREATED': {
      // Server's ROOM_CREATED carries room_code + your_occupant_id +
      // your_client_uuid + room_state (snapshot including occupants).
      // Use the snapshot rather than synthesizing from local identity
      // so the rendered name matches what the server actually persisted.
      const selfId = action.payload.your_occupant_id;
      const room = action.payload.room_state;
      const occupants = (room?.occupants || []).map((o) => toViewOccupant(o, selfId));
      const self = occupants.find((o) => o.is_you);
      return {
        ...INITIAL,
        status: 'in_room',
        roomCode: action.payload.room_code,
        selfOccupantId: selfId,
        occupants,
        isHost: !!self?.is_host,
        settings: room?.settings || { ...DEFAULT_SETTINGS },
        phase: 'waiting',
      };
    }
    case 'ROOM_JOINED':
    case 'ROOM_SNAPSHOT': {
      const room = action.payload.room_state;
      const selfId = action.payload.your_occupant_id ?? state.selfOccupantId;
      const occupants = (room.occupants || []).map((o) => toViewOccupant(o, selfId));
      const self = occupants.find((o) => o.is_you);
      return {
        ...state,
        status: 'in_room',
        roomCode: room.room_code,
        selfOccupantId: selfId,
        occupants,
        isHost: !!self?.is_host,
        settings: room.settings || state.settings,
        phase: room.phase === 'in_game' ? 'in_game'
             : room.phase === 'starting' ? 'starting'
             : 'waiting',
        removed: null,
        error: null,
      };
    }
    case 'OCCUPANT_JOINED': {
      const newOcc = toViewOccupant(action.payload.occupant, state.selfOccupantId);
      // Idempotent: don't double-add (causation from our own JOIN).
      if (state.occupants.some((o) => o.occupant_id === newOcc.occupant_id)) return state;
      return { ...state, occupants: [...state.occupants, newOcc] };
    }
    case 'OCCUPANT_LEFT': {
      const id = action.payload.occupant_id;
      const isLocal = state.selfOccupantId === id;
      if (isLocal) {
        const reason = action.payload.reason === 'kicked' ? 'kicked' : 'room_closed';
        return { ...INITIAL, removed: { reason } };
      }
      return {
        ...state,
        occupants: state.occupants.filter((o) => o.occupant_id !== id),
      };
    }
    case 'HOST_CHANGED': {
      const newHost = action.payload.new_host_occupant_id;
      const occupants = state.occupants.map((o) => ({
        ...o,
        is_host: o.occupant_id === newHost,
      }));
      const self = occupants.find((o) => o.is_you);
      return { ...state, occupants, isHost: !!self?.is_host };
    }
    case 'SETTINGS_UPDATED':
      return { ...state, settings: { ...state.settings, ...action.payload.settings } };
    case 'READY_CHANGED': {
      // Server payload is { occupant_id, ready }, the snapshot's
      // occupant has is_ready but the per-event payload uses `ready`.
      const id = action.payload.occupant_id;
      const ready = !!action.payload.ready;
      return {
        ...state,
        occupants: state.occupants.map((o) =>
          o.occupant_id === id ? { ...o, ready } : o),
      };
    }
    case 'TEAM_CHANGED': {
      // #8b: server broadcasts TEAM_CHANGED { occupant_id, team } on every
      // team pick/un-pick, it does NOT re-send a full snapshot, so we
      // apply the delta here (mirrors READY_CHANGED). The snapshot path
      // (toViewOccupant) carries the initial/rejoin team.
      const id = action.payload.occupant_id;
      const team = action.payload.team ?? null;
      return {
        ...state,
        occupants: state.occupants.map((o) =>
          o.occupant_id === id ? { ...o, team } : o),
      };
    }
    case 'CONNECTION_STATUS_CHANGED': {
      const id = action.payload.occupant_id;
      // Batch D: the server sends `{ occupant_id, status }` (room.js
      // _setConnectionStatus); older payloads carried `connection_status`.
      // Accept both, otherwise every broadcast collapsed to 'connected'
      // and away/reconnecting states could never render.
      const cs = action.payload.connection_status || action.payload.status || 'connected';
      return {
        ...state,
        occupants: state.occupants.map((o) =>
          o.occupant_id === id ? { ...o, connection_status: cs } : o),
      };
    }
    case 'GAME_STARTING': {
      // M3-14 AC #3: reset every occupant's ready flag on transition.
      // M4-11: also flip to `in_game` so the client mounts the
      // NetworkedGameView immediately. The server hops STARTING →
      // IN_GAME synchronously after START_GAME (M4-9), and the
      // NetworkedGameView waits for the GAME_INITIALIZED snapshot
      // before it tries to render anything.
      // M4-11: also stash seat assignments so the local player knows
      // which seat is theirs (bots get assigned mid-flight too).
      // Item 2 (round 3): the room sends no human seats when a game starts,
      // so work them out now, while everyone is still listed. The game
      // keeps them, so a player who leaves before acting keeps their name.
      const seats = seatsAtGameStart(state.occupants, action.payload?.bot_assignments);
      return {
        ...state,
        phase: 'in_game',
        lastGame: null,
        viewerSeatIndex: null,
        occupants: state.occupants.map((o) => ({ ...o, ready: false, seat_index: seats.get(o.occupant_id) ?? o.seat_index })),
      };
    }
    case 'GAME_INITIALIZED': {
      const viewerSeatIndex = action.payload?.your_seat_index ?? state.viewerSeatIndex;
      // The viewer's own seat checks the worked-out seats: if they
      // disagree, none of them can be trusted.
      const self = state.occupants.find((o) => o.is_you);
      let occupants = state.occupants;
      if (typeof action.payload?.your_seat_index === 'number' && self?.seat_index != null
          && self.seat_index !== action.payload.your_seat_index) {
        console.warn(`[useRoomWs] room list puts you at seat ${self.seat_index}, the game at ${action.payload.your_seat_index}; dropping the room seats`);
        occupants = occupants.map((o) => ({ ...o, seat_index: null }));
      }
      // Item 7: a client that reattaches during the final recap never gets
      // GAME_ENDED. The reattach snapshot holds the result instead.
      const snap = action.payload?.game_state_snapshot;
      const over = action.payload?.reattach && (snap?.game_over || snap?.score_summary?.game_over);
      const lastGame = over
        ? lastGameFrom(snap?.scores, snap?.winning_team, viewerSeatIndex)
        : null;
      return { ...state, occupants, viewerSeatIndex, lastGame: lastGame ?? state.lastGame };
    }
    case 'GAME_ENDED': {
      const seat = state.viewerSeatIndex ?? state.occupants.find((o) => o.is_you)?.seat_index;
      return { ...state, lastGame: lastGameFrom(action.payload?.final_scores, action.payload?.winner_team, seat) };
    }
    case 'DISMISS_REMOVAL':
      return { ...INITIAL };
    case 'RESET':
      return { ...INITIAL };
    default:
      return state;
  }
}

/**
 * The socket URL for a page location. Same host as the page, ws scheme
 * matching http(s). Local dev runs the server on a fixed port: 3030, or
 * the build setting VITE_WS_PORT so a test build can use its own server.
 * Production deploys behind a reverse proxy on the same origin and never
 * reads VITE_WS_PORT. T4 moved the upgrade path from `/` to `/ws` so
 * nginx can scope the upgrade headers narrowly.
 *
 * @param {{ hostname: string, protocol: string, host: string }} loc
 * @param {string|undefined} wsPort  VITE_WS_PORT, unset or '' for 3030
 * @returns {string}
 */
export function wsUrlFor(loc, wsPort) {
  if (loc.hostname === '127.0.0.1' || loc.hostname === 'localhost') {
    return `ws://${loc.hostname}:${localWsPort(wsPort)}/ws`;
  }
  const scheme = loc.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${loc.host}/ws`;
}

/**
 * @param {string|undefined} wsPort
 * @returns {number}
 */
function localWsPort(wsPort) {
  if (wsPort === undefined || wsPort === '') return 3030;
  const port = Number(wsPort);
  if (!/^\d+$/.test(wsPort) || port < 1 || port > 65535) {
    throw new Error(`VITE_WS_PORT must be a port number from 1 to 65535, got "${wsPort}"`);
  }
  return port;
}

function defaultWsUrl() {
  const wsPort = import.meta.env?.VITE_WS_PORT;
  if (typeof window === 'undefined') return `ws://127.0.0.1:${localWsPort(wsPort)}/ws`;
  return wsUrlFor(window.location, wsPort);
}

/**
 * @param {{ url?: string }} [opts]
 */
export function useRoomWs(opts = {}) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  /** @type {{ current: ReturnType<typeof createRoomWsService>|null }} */
  const serviceRef = useRef(null);

  // Create the WS service once. Identity is captured at hook-init time;
  // the user can keep editing it in the IdentityPanel until they Create
  // or Join, at which point the snapshot is what we send.
  useEffect(() => {
    if (serviceRef.current) return;
    const identity = getOrCreateIdentity();
    const svc = createRoomWsService({
      url: opts.url || defaultWsUrl(),
      identity,
      onEvent: (event) => {
        // Dispatch every server event whose type we recognise.
        if (!event || typeof event !== 'object') return;
        switch (event.type) {
          case 'ROOM_CREATED':
            dispatch({ type: 'ROOM_CREATED', payload: event.payload });
            break;
          case 'ROOM_JOINED':
          case 'ROOM_SNAPSHOT':
            dispatch({ type: event.type, payload: event.payload });
            break;
          case 'GAME_ENDED':
            dispatch({ type: 'GAME_ENDED', payload: event.payload });
            break;
          case 'GAME_INITIALIZED':
            dispatch({ type: 'GAME_INITIALIZED', payload: event.payload });
            break;
          case 'COMMAND_REJECTED':
            dispatch({
              type: 'COMMAND_REJECTED',
              commandType: event.payload?.command_type ?? null,
              error: event.payload?.error || { code: 'UNKNOWN', message: 'unknown error' },
            });
            break;
          case 'OCCUPANT_JOINED':
          case 'OCCUPANT_LEFT':
          case 'HOST_CHANGED':
          case 'SETTINGS_UPDATED':
          case 'READY_CHANGED':
          case 'TEAM_CHANGED':
          case 'CONNECTION_STATUS_CHANGED':
          case 'GAME_STARTING':
            dispatch({ type: event.type, payload: event.payload });
            break;
          // COMMAND_ACCEPTED, PONG, PROTOCOL_MISMATCH are noted but
          // don't affect derived room state.
        }
      },
      onError: serviceErrorHandler(dispatch),
    });
    serviceRef.current = svc;
    // e2e seam: the C3 connection-loss specs sever/restore the live socket
    // through this handle (Playwright's setOffline does not reliably kill
    // an established WebSocket, so emulation can't drive the banner).
    if (typeof window !== 'undefined') window.__euchreWsService = svc;
    svc.connect();
    return () => {
      try { svc.disconnect(); } catch (err) {
        console.warn('[useRoomWs] disconnect on unmount failed:', err instanceof Error ? err.message : err);
      }
      if (typeof window !== 'undefined' && window.__euchreWsService === svc) {
        window.__euchreWsService = null;
      }
      serviceRef.current = null;
    };
  }, [opts.url]);

  const createRoom = useCallback(() => {
    const svc = serviceRef.current;
    if (!svc) return;
    const identity = getOrCreateIdentity();
    dispatch({ type: 'CREATE_PENDING' });
    svc.send({
      type: 'CREATE_ROOM',
      payload: { identity, settings: { ...DEFAULT_SETTINGS } },
    });
  }, []);

  const joinRoom = useCallback((/** @type {string} */ rawCode) => {
    const svc = serviceRef.current;
    if (!svc) return rawCode;
    const code = (rawCode || '').toUpperCase();
    const identity = getOrCreateIdentity();
    dispatch({ type: 'JOIN_PENDING', code });
    svc.send({
      type: 'JOIN_ROOM',
      payload: { room_code: code, identity },
    });
    return code;
  }, []);

  useEffect(() => {
    if (state.status !== 'joining') return undefined;
    return scheduleJoinTimeout(() => dispatch({ type: 'JOIN_TIMEOUT' }));
  }, [state.status]);

  const leaveRoom = useCallback(() => {
    const svc = serviceRef.current;
    clearLastRoom('leave');
    // Best-effort server notify. Leaving must ALWAYS tear down the local
    // state and return to the lobby, never let a send error (e.g. a
    // closing/closed socket) throw out of the click handler and strand the
    // player on a frozen game view, so RESET runs in a finally.
    try {
      if (svc) svc.send({ type: 'LEAVE_ROOM', payload: {} });
    } catch (err) {
      // RESET below still runs; the server's grace removes the seat.
      console.warn('[useRoomWs] sending LEAVE_ROOM failed:', err instanceof Error ? err.message : err);
    } finally {
      dispatch({ type: 'RESET' });
    }
  }, []);

  const clearError = useCallback(() => dispatch({ type: 'CLEAR_ERROR' }), []);

  const updateSettings = useCallback((/** @type {Partial<{target_score:7|10, hand_timer_seconds:20|30|60|null, stick_the_dealer:boolean, bot_fill:boolean}>} */ patch) => {
    const svc = serviceRef.current;
    if (!svc) return;
    svc.send({
      type: 'UPDATE_SETTINGS',
      payload: { settings: patch },
    });
  }, []);

  const startGame = useCallback(() => {
    const svc = serviceRef.current;
    if (!svc) return;
    svc.send({ type: 'START_GAME', payload: {} });
  }, []);

  const kickOccupant = useCallback((/** @type {string} */ target_occupant_id) => {
    const svc = serviceRef.current;
    if (!svc) return;
    svc.send({
      type: 'KICK_OCCUPANT',
      payload: { target_occupant_id },
    });
  }, []);

  const setReady = useCallback((/** @type {boolean} */ ready) => {
    const svc = serviceRef.current;
    if (!svc) return;
    svc.send({
      type: 'SET_READY',
      payload: { ready: !!ready },
    });
  }, []);

  // #8b: pick (or un-pick) Team A/B. team ∈ {null, 'A', 'B'}. The disabled
  // Join-button guard normally prevents a TEAM_FULL rejection; if one slips
  // through it surfaces transiently via the COMMAND_REJECTED → error path
  // (we stay in the room, no crash, see the reducer's in_room branch).
  const setTeam = useCallback((/** @type {null|'A'|'B'} */ team) => {
    const svc = serviceRef.current;
    if (!svc) return;
    svc.send({
      type: 'SET_TEAM',
      payload: { team: team ?? null },
    });
  }, []);

  const dismissRemoval = useCallback(() => {
    dispatch({ type: 'DISMISS_REMOVAL' });
  }, []);

  // ── #7: shareable ?room=CODE URL ────────────────────────────────────
  // Reflect the live room code in the address bar with replaceState (NOT
  // pushState) so the URL is shareable WITHOUT pushing a history entry,
  // that would collide with the #5b overlay pushState MARKER, which owns
  // the back-stack. Entering a room writes ?room=CODE; leaving clears it
  // back to '/'. replaceState fires no popstate, so the overlay listener
  // never sees it.
  const inRoom = state.status === 'in_room' && !!state.roomCode;
  // True once this page has been inside a room. A ?room= code is stale only
  // after leaving that room or after a join failed; on a fresh load the code
  // stays in the address bar while the auto-join is in flight, so a reload in
  // that window (before ROOM_JOINED writes the code back) still knows which
  // room to rejoin. Stripping it on the first render lost the code for a
  // reload that came within a few hundred ms of the occupant list appearing.
  const wasInRoomRef = useRef(false);
  // The room code lifted from ?room= at load, or null when the query holds no
  // valid code. Read by the address-bar effect below and by the auto-join.
  const urlRoomCodeRef = useRef(
    typeof window !== 'undefined' ? parseRoomCodeFromUrl(window.location.search) : null,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    const { pathname, search } = window.location;
    if (inRoom) {
      wasInRoomRef.current = true;
      const want = `?room=${state.roomCode}`;
      if (search !== want) {
        window.history.replaceState(window.history.state, '', `${pathname}${want}`);
      }
    } else if (search && (wasInRoomRef.current || state.status === 'error' || !urlRoomCodeRef.current)) {
      // Out of a room we were in, holding a code the server refused, or carrying
      // a query with no valid code at all: strip it back to the bare path so a
      // reload won't retry a stale code.
      window.history.replaceState(window.history.state, '', pathname);
    }
  }, [inRoom, state.roomCode, state.status]);

  // ── #7: one-shot auto-join from ?room=CODE ──────────────────────────
  // On the FIRST render after load, if the URL carries a valid room code,
  // auto-join it exactly once. The ref makes this a hard one-shot: it can
  // never re-fire on a re-render, effect re-run, browser back/forward, or
  // reload-into-a-room. shouldAutoJoin() additionally no-ops when we're
  // already in that room (the share-while-in-room / second-tab case, which
  // the server would reject with ALREADY_CONNECTED). joinRoom queues the
  // command if the socket hasn't opened yet, so no connect-timing race.
  const autoJoinAttemptedRef = useRef(false);
  useEffect(() => {
    const urlCode = urlRoomCodeRef.current;
    const ok = shouldAutoJoin({
      urlCode,
      hasAttempted: autoJoinAttemptedRef.current,
      inRoom,
      currentRoomCode: state.roomCode,
    });
    if (!ok || !urlCode) return;
    autoJoinAttemptedRef.current = true;
    joinRoom(urlCode);
    // Depend on `inRoom` only so a late-arriving room state can flip the
    // guard, but the ref ensures the body runs at most once regardless.
  }, [inRoom, joinRoom, state.roomCode]);

  // ── B3b F7: remember the last room code (with a timestamp, renewed
  // every minute) so the home page can offer "Rejoin your game" after a
  // closed tab, and forget it when the room is gone for this player.
  // Storage failures log a console warning inside last-room.js.
  useEffect(
    () => lastRoomEffect(state),
    [state.status, state.roomCode, state.removed, state.error, state.joiningCode],
  );

  // ── W5 + fix9 J1: a kick/removal must forget the room code too (the service
  // cannot tell our OCCUPANT_LEFT from someone else's, so the hook
  // tells it). Otherwise the next reconnect replays JOIN_ROOM into a
  // room we were removed from. LEAVE_ROOM forgets inside the service's
  // sendRaw; this covers the kick path. fix9 J1 extends the same call
  // to a NOT_IN_ROOM exit (see needsRoomForget): the B2-13 reducer arm
  // leaves the room view with roomCode null but `removed` null, so the
  // old `state.removed` check never fired and the reconnect rejoined
  // the dead room.
  useEffect(() => {
    roomForgetEffect(state, serviceRef.current);
  }, forgetRoomEffectDeps(state));

  return useMemo(() => {
    // M4-11: expose the WS service + the local player's seat_index so
    // NetworkedGameView can wire the M4 game hook to the same socket.
    const self = state.occupants.find((o) => o.is_you);
    const selfSeatIndex = self?.seat_index ?? null;
    return {
      status: state.status,
      roomCode: state.roomCode,
      occupants: state.occupants,
      isHost: state.isHost,
      settings: state.settings,
      phase: state.phase,
      error: state.error,
      removed: state.removed,
      lastGame: state.lastGame,
      selfSeatIndex,
      // #7: the room code lifted from ?room= at load (or null). RoomLobby
      // seeds the join-code field with this so the field is prefilled while
      // the one-shot auto-join above runs in the background.
      initialJoinCode: urlRoomCodeRef.current,
      service: serviceRef.current,
      createRoom,
      joinRoom,
      leaveRoom,
      clearError,
      updateSettings,
      startGame,
      kickOccupant,
      dismissRemoval,
      setReady,
      setTeam,
    };
  }, [state, createRoom, joinRoom, leaveRoom, clearError, updateSettings, startGame, kickOccupant, dismissRemoval, setReady, setTeam]);
}
